import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EnhancedTranslationManager } from "../utils/enhancedTranslationManager";

interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (language: string) => void;
  isTranslating: boolean;
  isGoogleTranslateLoaded: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

// Global flag to prevent multiple initializations
let isGoogleTranslateInitializing = false;
let isGoogleTranslateReady = false;

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState("en");
  const [isTranslating, setIsTranslating] = useState(false);
  const [isGoogleTranslateLoaded, setIsGoogleTranslateLoaded] = useState(false);
  const [hasInitialTranslation, setHasInitialTranslation] = useState(false);
  const [isSwitchingLanguage, setIsSwitchingLanguage] = useState(false);

  // Extract language from URL path
  const getLanguageFromPath = (pathname: string): string => {
    const pathSegments = pathname.split("/").filter(Boolean);
    const firstSegment = pathSegments[0];

    if (["en", "si", "ta"].includes(firstSegment)) {
      return firstSegment;
    }
    return "en"; // Default to English
  };

  // Get current path without language prefix
  const getPathWithoutLanguage = (pathname: string): string => {
    const pathSegments = pathname.split("/").filter(Boolean);
    const firstSegment = pathSegments[0];

    if (["en", "si", "ta"].includes(firstSegment)) {
      return "/" + pathSegments.slice(1).join("/");
    }
    return pathname;
  };

  // Helper function to clear translation state
  const clearTranslationState = () => {
    const body = document.body;
    if (body) {
      body.classList.remove("translated-ltr", "translated-rtl");
      body.style.direction = "ltr";
    }

    // Remove any Google Translate styling
    const translatedElements = document.querySelectorAll('[class*="goog-te"]');
    translatedElements.forEach((el) => {
      if (el instanceof HTMLElement) {
        el.style.display = "none";
      }
    });
  };

  // Initialize Google Translate only when needed
  const initializeGoogleTranslate = () => {
    if (isGoogleTranslateInitializing || isGoogleTranslateReady) {
      return;
    }

    isGoogleTranslateInitializing = true;

    // Prevent conflicts with existing global function
    if (!(window as any).googleTranslateElementInit) {
      (window as any).googleTranslateElementInit = () => {
        try {
          if (!(window as any).google?.translate?.TranslateElement) {
            console.error("Google Translate API not available");
            isGoogleTranslateInitializing = false;
            return;
          }

          new (window as any).google.translate.TranslateElement(
            {
              pageLanguage: "en",
              includedLanguages: "en,si,ta",
              layout: (window as any).google.translate.TranslateElement
                .InlineLayout.VERTICAL,
              autoDisplay: false,
              multilanguagePage: false, // Disable auto-detection
              gaTrack: false, // Disable Google Analytics tracking
              gaId: null, // No Google Analytics ID
            },
            "google_translate_element"
          );

          isGoogleTranslateReady = true;
          setIsGoogleTranslateLoaded(true);
          console.log("✅ Google Translate initialized successfully");
        } catch (error) {
          console.error("❌ Error initializing Google Translate:", error);
          isGoogleTranslateInitializing = false;
        }
      };
    }

    // Load script only if not already loaded
    if (!document.getElementById("google-translate-script")) {
      const script = document.createElement("script");
      script.id = "google-translate-script";
      script.src =
        "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      script.onerror = () => {
        console.error("❌ Failed to load Google Translate script");
        isGoogleTranslateInitializing = false;
        setIsGoogleTranslateLoaded(false);
      };
      script.onload = () => {
        console.log("📦 Google Translate script loaded");
        // Give it a moment to initialize
        setTimeout(() => {
          if ((window as any).google?.translate?.TranslateElement) {
            isGoogleTranslateReady = true;
            setIsGoogleTranslateLoaded(true);
          }
        }, 1000);
      };
      document.head.appendChild(script);
    } else {
      // Script already loaded, check if Google Translate is available
      if ((window as any).google?.translate?.TranslateElement) {
        isGoogleTranslateReady = true;
        setIsGoogleTranslateLoaded(true);
      } else {
        // Script loaded but not ready, wait a bit
        setTimeout(() => {
          if ((window as any).google?.translate?.TranslateElement) {
            isGoogleTranslateReady = true;
            setIsGoogleTranslateLoaded(true);
          }
        }, 1000);
      }
    }
  };

  // Initialize language from URL on mount
  useEffect(() => {
    const urlLanguage = getLanguageFromPath(location.pathname);
    console.log(`🌐 Detected language from URL: ${urlLanguage}`);

    // Set language from URL
    setCurrentLanguage(urlLanguage);
    localStorage.setItem("mealbridge-language", urlLanguage);

    // Clear any existing translation state immediately
    clearTranslationState();

    // If URL has language prefix, ensure we're on the right path
    if (
      urlLanguage !== "en" &&
      !location.pathname.startsWith(`/${urlLanguage}`)
    ) {
      const currentPath = getPathWithoutLanguage(location.pathname);
      navigate(`/${urlLanguage}${currentPath}`, { replace: true });
    } else if (urlLanguage === "en" && location.pathname.startsWith("/en")) {
      // Remove /en prefix for English
      const currentPath = getPathWithoutLanguage(location.pathname);
      navigate(currentPath || "/", { replace: true });
    }

    // For non-English URLs, we need to trigger translation
    if (urlLanguage !== "en") {
      console.log(
        `🔄 Non-English URL detected, will trigger translation to ${urlLanguage}`
      );
      // Initialize Google Translate and trigger translation
      if (!isGoogleTranslateReady && !isGoogleTranslateInitializing) {
        initializeGoogleTranslate();
      }
    }
  }, [location.pathname, navigate]);

  // Handle redirects for non-English content on English URLs
  useEffect(() => {
    const urlLanguage = getLanguageFromPath(location.pathname);

    // If we're on an English URL but content is translated, redirect to appropriate language URL
    if (urlLanguage === "en") {
      const body = document.body;
      const isTranslated =
        body?.classList.contains("translated-ltr") ||
        body?.classList.contains("translated-rtl") ||
        document.querySelector('[class*="goog-te"]') !== null;

      if (isTranslated) {
        console.log(
          "🔄 Detected translated content on English URL, redirecting..."
        );
        // Check what language the content is actually in
        const combo = document.querySelector(
          ".goog-te-combo"
        ) as HTMLSelectElement;
        if (combo) {
          const currentLang = combo.value;
          if (currentLang === "si") {
            navigate(`/si${location.pathname}`, { replace: true });
          } else if (currentLang === "ta") {
            navigate(`/ta${location.pathname}`, { replace: true });
          }
        }
      }
    }
  }, [location.pathname, navigate]);

  // Handle initial language setup after Google Translate loads
  useEffect(() => {
    if (!isGoogleTranslateLoaded || hasInitialTranslation) {
      return;
    }

    // This effect should only run once on initial load.
    setHasInitialTranslation(true);

    const urlLanguage = getLanguageFromPath(location.pathname);
    console.log(`🇬🇧 Initializing with language from URL: ${urlLanguage}`);

    // Clear any existing translation state
    clearTranslationState();

    // Force clear Google Translate cookies to prevent auto-detection
    const cookies = [
      "googtrans",
      "googtrans=/en/en",
      "googtrans=/en/si",
      "googtrans=/en/ta",
      "googtrans=/si/en",
      "googtrans=/ta/en",
      "googtrans=/si/si",
      "googtrans=/ta/ta",
    ];

    cookies.forEach((cookie) => {
      document.cookie = `${cookie}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      document.cookie = `${cookie}=; Path=/; Domain=${window.location.hostname}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      document.cookie = `${cookie}=; Path=/; Domain=.${window.location.hostname}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
    });

    // Set the appropriate language cookie
    const cookieValue = urlLanguage === "en" ? "/en/en" : `/en/${urlLanguage}`;
    document.cookie = `googtrans=${cookieValue}; Path=/; Domain=${window.location.hostname}`;
    document.cookie = `googtrans=${cookieValue}; Path=/; Domain=.${window.location.hostname}`;
    document.cookie = `googtrans=${cookieValue}; Path=/`;

    console.log(`🍪 Set Google Translate cookie to: ${cookieValue}`);

    // If it's a non-English URL, trigger translation after a delay
    if (urlLanguage !== "en") {
      setTimeout(() => {
        console.log(
          `🔄 Triggering translation for URL language: ${urlLanguage}`
        );
        triggerTranslation(urlLanguage);
      }, 1000);
    }
  }, [isGoogleTranslateLoaded, hasInitialTranslation, location.pathname]);

  const triggerTranslation = (languageCode: string) => {
    if (!isGoogleTranslateReady) {
      console.log("⚠️ Google Translate not ready, retrying in 500ms...");
      setTimeout(() => triggerTranslation(languageCode), 500);
      return;
    }

    console.log(`🔄 Triggering translation to: ${languageCode}`);

    // For English, we need to reset to original language completely
    if (languageCode === "en") {
      console.log("🇬🇧 Switching to English - clearing translation state");
      setIsTranslating(true);

      // Use the enhanced translation manager for better English translation
      const enhancedManager = EnhancedTranslationManager.getInstance();
      enhancedManager
        .restoreToEnglish()
        .then(() => {
          console.log("✅ English translation completed");
          setIsTranslating(false);
          setCurrentLanguage("en");

          // Update localStorage to persist the language choice
          localStorage.setItem("mealbridge-language", "en");
        })
        .catch((error) => {
          console.error("❌ English translation failed:", error);
          setIsTranslating(false);
          // Update state even if translation fails
          setCurrentLanguage("en");
          localStorage.setItem("mealbridge-language", "en");
          console.log("🔄 English state updated despite translation issues");

          // As a last resort, try to force a clean English state
          setTimeout(() => {
            console.log("🔄 Attempting last resort English translation...");
            // Force clear all translation state
            const body = document.body;
            if (body) {
              body.classList.remove("translated-ltr", "translated-rtl");
              body.style.direction = "ltr";
            }

            // Hide all Google Translate elements
            const googleElements =
              document.querySelectorAll('[class*="goog-te"]');
            googleElements.forEach((el) => {
              if (el instanceof HTMLElement) {
                el.style.display = "none";
              }
            });

            // Try to find and reset the combo one more time
            const combo = document.querySelector(
              ".goog-te-combo"
            ) as HTMLSelectElement;
            if (combo) {
              console.log("🔍 Found combo for last resort reset");
              console.log("🔍 Current combo value:", combo.value);
              console.log(
                "🔍 Available options:",
                Array.from(combo.options).map((opt) => ({
                  value: opt.value,
                  text: opt.text,
                }))
              );

              // Try to find English option
              const englishOption = Array.from(combo.options).find(
                (opt) =>
                  opt.text.toLowerCase().includes("english") ||
                  opt.value === "0" ||
                  opt.value === "en" ||
                  opt.value === ""
              );

              if (englishOption) {
                console.log(
                  "✅ Found English option for last resort:",
                  englishOption.value,
                  englishOption.text
                );
                combo.value = englishOption.value;
              } else {
                console.log(
                  "⚠️ English option not found, trying common values"
                );
                const englishValues = ["0", "", "en", "auto", "en-US", "en-GB"];
                for (const value of englishValues) {
                  if (
                    Array.from(combo.options).some((opt) => opt.value === value)
                  ) {
                    combo.value = value;
                    console.log("✅ Set combo to:", value);
                    break;
                  }
                }
              }

              // Dispatch multiple events
              combo.dispatchEvent(new Event("change", { bubbles: true }));
              combo.dispatchEvent(new Event("input", { bubbles: true }));
              combo.dispatchEvent(new Event("click", { bubbles: true }));

              // Force focus and blur
              combo.focus();
              combo.blur();
              combo.focus();
              combo.blur();

              // Try custom event
              combo.dispatchEvent(
                new CustomEvent("googtrans", {
                  detail: { value: combo.value, language: "en" },
                  bubbles: true,
                })
              );
            } else {
              console.log("❌ No combo found for last resort reset");
            }
          }, 2000);
        });

      return;
    }

    // For non-English languages (Sinhala/Tamil) - use enhanced AI translation manager
    setIsTranslating(true);
    const enhancedManager = EnhancedTranslationManager.getInstance();
    enhancedManager
      .translateTo(languageCode as "si" | "ta")
      .then(() => {
        console.log(`✅ Translation to ${languageCode} completed`);
        setIsTranslating(false);
        setCurrentLanguage(languageCode);
      })
      .catch((error) => {
        console.error(`❌ Translation to ${languageCode} failed:`, error);
        setIsTranslating(false);
      });
  };

  const setLanguage = (language: string) => {
    console.log(`🌐 Setting language to: ${language}`);

    // Validate language code
    const validLanguages = ["en", "si", "ta"];
    if (!validLanguages.includes(language)) {
      console.error(`❌ Invalid language code: ${language}`);
      return;
    }

    // Prevent unnecessary translation if we're already in the target language
    if (currentLanguage === language && !isSwitchingLanguage) {
      console.log(`✅ Already in ${language}, no change needed`);
      return;
    }

    setIsSwitchingLanguage(true);
    setIsTranslating(true);
    setCurrentLanguage(language);

    // Save language preference immediately
    localStorage.setItem("mealbridge-language", language);
    console.log(`💾 Saved language preference: ${language}`);

    // Update URL based on language
    const currentPath = getPathWithoutLanguage(location.pathname);
    const newPath =
      language === "en" ? currentPath || "/" : `/${language}${currentPath}`;

    // Navigate to new URL
    navigate(newPath, { replace: true });
    console.log(`🔗 Updated URL to: ${newPath}`);

    // For English, we want to ensure clean state
    if (language === "en") {
      console.log("🇬🇧 Switching to English - will clean translate state");

      // Set a session flag to prevent auto-translation after page reload
      sessionStorage.setItem("mealbridge-switching-to-english", "true");

      // Clear any existing translation state
      clearTranslationState();

      // Force clear Google Translate cookies
      const cookies = [
        "googtrans",
        "googtrans=/en/en",
        "googtrans=/en/si",
        "googtrans=/en/ta",
        "googtrans=/si/en",
        "googtrans=/ta/en",
        "googtrans=/si/si",
        "googtrans=/ta/ta",
      ];

      cookies.forEach((cookie) => {
        document.cookie = `${cookie}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
        document.cookie = `${cookie}=; Path=/; Domain=${window.location.hostname}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
        document.cookie = `${cookie}=; Path=/; Domain=.${window.location.hostname}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      });

      // Set English as the default language
      document.cookie =
        "googtrans=/en/en; Path=/; Domain=" + window.location.hostname;
      document.cookie =
        "googtrans=/en/en; Path=/; Domain=." + window.location.hostname;
      document.cookie = "googtrans=/en/en; Path=/";

      // Try to reset Google Translate combo if available
      const combo = document.querySelector(
        ".goog-te-combo"
      ) as HTMLSelectElement;
      if (combo) {
        console.log("🔍 Found combo, resetting to English");

        // Try different values that might represent English
        const englishValues = ["0", "", "en", "auto"];

        for (const value of englishValues) {
          combo.value = value;
          combo.dispatchEvent(new Event("change", { bubbles: true }));
          combo.dispatchEvent(new Event("input", { bubbles: true }));
        }

        console.log("✅ English reset attempted");
      }

      // Force page reload to ensure clean English state
      setTimeout(() => {
        console.log("🔄 Reloading page to ensure clean English state");
        window.location.reload();
      }, 500);

      return; // Don't initialize Google Translate for English
    }

    // For non-English languages, initialize Google Translate if not already done
    if (!isGoogleTranslateReady && !isGoogleTranslateInitializing) {
      console.log("🔄 Initializing Google Translate for translation...");
      initializeGoogleTranslate();
    }

    // Trigger translation if Google Translate is ready
    if (isGoogleTranslateReady) {
      setTimeout(() => triggerTranslation(language), 300);
    } else {
      console.log("⚠️ Google Translate not ready, will retry when available");
      // Set up a retry mechanism
      const retryInterval = setInterval(() => {
        if (isGoogleTranslateReady) {
          clearInterval(retryInterval);
          triggerTranslation(language);
        }
      }, 500);

      // Clear retry after 10 seconds
      setTimeout(() => clearInterval(retryInterval), 10000);
    }

    // Reset translating state with better timing
    setTimeout(
      () => {
        setIsTranslating(false);
        setIsSwitchingLanguage(false);
      },
      language === "en" ? 2000 : 1500
    );
  };

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        setLanguage,
        isTranslating,
        isGoogleTranslateLoaded,
      }}
    >
      {/* Google Translate Element - Hidden but accessible */}
      <div
        id="google_translate_element"
        className="google-translate-hidden"
      ></div>
      {children}
    </LanguageContext.Provider>
  );
};
